import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Loader2, Pencil, Play, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { FORMATS, ReportDialog, SCHEDULES } from "@/components/ReportDialog";
import { api, errMsg, fdatetime } from "@/lib/api";
import { usePerm } from "@/context/AuthContext";

const FMT = Object.fromEntries(FORMATS);
const NEW = { name: "", sections: ["stats"], formats: ["pdf"], ai_ids: [], schedule: "none", recipients: [], ai_summary: true };

const download = async (run, fmt) => {
  try {
    const r = await api.get(`/reports/runs/${run.id}/file/${fmt}`, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || `report.${fmt}`;
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click(); URL.revokeObjectURL(url);
  } catch (e) { toast.error("Download non riuscito"); }
};

const RunInfo = ({ run, testId }) => {
  if (!run) return <span className="text-xs text-slate-400">Mai generato</span>;
  if (run.status === "running") return <span className="inline-flex items-center gap-1 text-xs text-blue-600" data-testid={`${testId}-running`}><Loader2 className="h-3.5 w-3.5 animate-spin" />Generazione in corso...</span>;
  if (run.status === "error") return <span className="text-xs text-rose-600" data-testid={`${testId}-error`}>Errore: {run.error}</span>;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid={`${testId}-done`}>
      <span>{fdatetime(run.finished_at)} · {run.trigger}{run.sent_to?.length ? ` · inviato a ${run.sent_to.length}` : ""}</span>
      {Object.keys(run.files || {}).map((f) => (
        <Button key={f} size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`${testId}-download-${f}`} onClick={() => download(run, f)}><Download className="mr-1 h-3 w-3" />{FMT[f]}</Button>
      ))}
    </div>
  );
};

const SharedRun = ({ id }) => {
  const [run, setRun] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/reports/runs/${id}`).then((r) => setRun(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  return (
    <Panel className="mb-6 border-blue-200" title={run ? `Report ricevuto: ${run.report_name}` : "Report ricevuto"} testId="shared-run">
      <div className="space-y-2 px-5 py-4">{err ? <span className="text-sm text-rose-600">{err}</span> : <>{run?.filters_text && <div className="text-xs text-slate-500">{run.filters_text}</div>}<RunInfo run={run} testId="shared-run" /></>}</div>
    </Panel>
  );
};

export default function Reports() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [params] = useSearchParams();
  const canWrite = usePerm("reports") === "write";
  const load = () => api.get("/reports").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rows?.some((r) => r.last_run?.status === "running")) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [rows]);
  const run = async (r, send) => {
    try { await api.post(`/reports/${r.id}/run`, { send }); toast.success(send ? "Report in preparazione: verrà inviato via email" : "Report in generazione"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare il report "${r.name}"?`)) return;
    try { await api.delete(`/reports/${r.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Report" subtitle="Componi, scarica e invia i risultati delle sezioni in PDF, Excel o CSV">
        {canWrite && <Button data-testid="report-new-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEdit(NEW)}><Plus className="mr-2 h-4 w-4" />Nuovo report</Button>}
      </PageHeader>
      {params.get("run") && <SharedRun id={params.get("run")} />}
      <Panel title="Report salvati" testId="reports-panel">
        {rows && !rows.length && <Empty text="Nessun report: creane uno con i filtri attivi" />}
        <div className="divide-y divide-slate-100">
          {(rows || []).map((r) => (
            <div key={r.id} className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between" data-testid={`report-row-${r.id}`}>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 font-medium text-slate-800"><FileText className="h-4 w-4 text-slate-400" />{r.name}</div>
                <div className="text-xs text-slate-500">{r.sections.length} sezioni · {r.formats.map((f) => FMT[f]).join(", ")} · {SCHEDULES[r.schedule]}{r.recipients.length ? ` · ${r.recipients.length} destinatari` : ""} · creato da {r.created_by_name}</div>
                <RunInfo run={r.last_run} testId={`report-${r.id}`} />
              </div>
              {canWrite && <div className="flex shrink-0 flex-wrap gap-1">
                <Button size="sm" variant="outline" data-testid={`report-run-${r.id}`} disabled={r.last_run?.status === "running"} onClick={() => run(r, false)}><Play className="mr-1 h-3.5 w-3.5" />Genera</Button>
                <Button size="sm" variant="outline" data-testid={`report-send-${r.id}`} disabled={r.last_run?.status === "running" || !r.recipients.length} onClick={() => run(r, true)}><Send className="mr-1 h-3.5 w-3.5" />Invia</Button>
                <Button size="sm" variant="outline" data-testid={`report-edit-${r.id}`} onClick={() => setEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" data-testid={`report-delete-${r.id}`} onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>}
            </div>
          ))}
        </div>
      </Panel>
      <ReportDialog report={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
